import { EntityFilterRow } from 'components/eventView/filters/EntityFilterRow';
import { EntityMultifiltersRow } from 'components/eventView/filters/EntityMultifiltersRow';
import { PageHeaderMini } from 'components/eventView/layout/PageHeaderMini';
import { EVOtherEntitiesPageContent } from 'components/eventView/pages/EVOtherEntitiesPageContent';
import { EventDescription } from 'components/guide/banner/EventDescription';
import { MainButton } from 'elements/button/MainButton';
import { ReverseButton } from 'elements/eventView/atomic/ReverseButton';
import { SearchInput } from 'elements/eventView/atomic/SearchInput';
import { PageContentLayoutMobile } from 'elements/eventView/layout/PageContentLayoutMobile';
import { StickyHeaderContentLayout } from 'elements/eventView/layout/StickyHeaderContentLayout';
import { StickyHeaderLayout } from 'elements/eventView/layout/StickyHeaderLayout';
import { motion } from 'framer-motion';
import { useAnalytics } from 'libs/utils/site/analytics';
import { EVDetailPageMobile } from 'pages/EventViewPage/EventViewPageMobile/EVDetailPageMobile';
import { useContext } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router';
import { CtxEventView } from 'session/ctxEventView';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    entityDefinitionId: string;
}

export const EVOtherEntitiesPageMobile = (props: Props) => {
    const ctxEventView = useContext(CtxEventView);
    const analytics = useAnalytics();
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const focusSearch = searchParams.get('focusSearch');

    const entityState = ctxEventView.entityStates[props.entityDefinitionId];
    const entityDefinition = ctxEventView.otherEntityDefinitions.find(
        (e) => e.id === props.entityDefinitionId
    );

    if (!entityState || !entityDefinition) {
        return null;
    }

    function resetFilters() {
        analytics.track('event_ui.filter.clicked', {
            type: 'reset'
        });

        entityState.setSearchTerm('');
        entityState.setFilterIds(null);
    }

    const isPartialListVisible =
        entityDefinition.searchable && entityState.visible?.length !== entityState.all?.length;

    return (
        <S_EVOtherEntitiesPageMobile className={props.className}>
            <StickyHeaderLayout>
                <PageHeaderMini>
                    {entityDefinition.searchable ? (
                        <SearchInput
                            value={entityState.searchTerm}
                            placeholder={entityDefinition.name}
                            onChange={(value) => {
                                entityState.setSearchTerm(value);
                            }}
                            autoFocus={focusSearch === '1'}
                        />
                    ) : (
                        <>{entityDefinition.name}</>
                    )}
                </PageHeaderMini>
            </StickyHeaderLayout>
            <StickyHeaderContentLayout headerHeight={64}>
                {entityDefinition.categories?.length > 0 && (
                    <>
                        {entityDefinition.multifilters ? (
                            <EntityMultifiltersRow entityId={entityDefinition.id} />
                        ) : (
                            <EntityFilterRow entityId={entityDefinition.id} isWide={false} />
                        )}
                    </>
                )}
                <S_Content>
                    {entityDefinition.description && (
                        <>
                            <S_EventDescription text={entityDefinition.description} />
                            <S_Space />
                        </>
                    )}
                    {entityDefinition.link && (
                        <>
                            <S_Link
                                key={entityDefinition.link.url}
                                href={entityDefinition.link.url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <S_MainButton
                                    type="event-secondary2"
                                    iconSet="far"
                                    iconName="arrow-up-right-from-square"
                                >
                                    {entityDefinition.link.text}
                                </S_MainButton>
                            </S_Link>
                            <S_Space />
                        </>
                    )}
                    {(isPartialListVisible || entityDefinition.isMapEntity) && (
                        <>
                            <S_Row
                                $align={(() => {
                                    if (isPartialListVisible && entityDefinition.isMapEntity) {
                                        return null;
                                    }
                                    if (isPartialListVisible) {
                                        return 'end';
                                    }
                                    return 'start';
                                })()}
                            >
                                {entityDefinition.isMapEntity && (
                                    <S_ReverseButton
                                        iconName={'location-dot'}
                                        onClick={() => {
                                            analytics.track('event_ui.navigate.clicked', {
                                                source: 'switch',
                                                destination: 'map'
                                            });
                                            navigate(`/event/${ctxEventView.eventPath}/map`);
                                        }}
                                        text={Localization.MAP_VIEW}
                                    />
                                )}
                                {isPartialListVisible && (
                                    <S_MainButton
                                        type="event-secondary"
                                        onClick={() => {
                                            resetFilters();
                                        }}
                                    >
                                        {Localization.REMOVE_FILTERS} ({entityState.visible?.length}
                                        /{entityState.all?.length})
                                    </S_MainButton>
                                )}
                            </S_Row>
                            <S_Space />
                        </>
                    )}
                    <S_EVOtherEntitiesPageContent entityId={entityDefinition.id} />
                </S_Content>
            </StickyHeaderContentLayout>
            <Routes location={location} key={location.pathname}>
                <Route path=":detailId" element={<S_EVDetailPageMobile />} />
            </Routes>
        </S_EVOtherEntitiesPageMobile>
    );
};

const S_EVOtherEntitiesPageMobile = styled(motion.div)`
    background-color: #ecf0f5;
    height: 100%;
`;

const S_EVDetailPageMobile = styled(EVDetailPageMobile)`
    position: relative;
    z-index: 4;
`;

const S_Content = styled(PageContentLayoutMobile)`
    padding: 15px 10px 150px 10px;
`;

const S_EventDescription = styled(EventDescription)`
    margin: 0px 0 0px 0;
    border-radius: 15px;
    background-color: #ffffff;
`;

const S_Link = styled.a`
    display: flex;
    justify-content: center;
    margin: 0 auto 0px auto;
    width: fit-content;
`;

const S_ReverseButton = styled(ReverseButton)``;
const S_MainButton = styled(MainButton)``;

const S_Row = styled.div<{ $align?: 'start' | 'end' }>`
    display: flex;
    justify-content: ${(props) => (props.$align ? `flex-${props.$align}` : 'space-between')};
    width: 100%;
    margin: 0px 0 0px 0;
`;

const S_Space = styled.div`
    height: 15px;
`;

const S_EVOtherEntitiesPageContent = styled(EVOtherEntitiesPageContent)`
    padding: 0 0 200px 0;
`;
